/**
 * Client-side renderer for the printable character sheet. This replaces the old
 * server-rendered MainController::sheetAction now that there is no PHP backend.
 *
 * Sources of the selection, in order:
 *   1. the `?characters=` URL parameter (comma-separated official IDs) — used by
 *      the in-app "open sheet" link and shareable for official scripts;
 *   2. otherwise the current script saved in localStorage (covers homebrew,
 *      which is local-only).
 *
 * Official characters are resolved from the static per-locale data feeds; custom
 * (homebrew) characters are resolved from the localStorage copy of the script.
 */
const STORE_KEY = "pocket-grimoire";
const TEAM_ORDER = ["townsfolk", "outsider", "minion", "demon", "traveller", "fabled", "loric"];
const OPEN_TEAMS = ["townsfolk", "outsider", "minion", "demon"];

function normaliseId(id) {
    return String(id == null ? "" : id).replace(/[-_]/g, "").toLowerCase();
}

function localeSegment() {
    return window.location.pathname.split("/").filter(Boolean)[0] || "en_GB";
}

function readStore() {
    try {
        return JSON.parse(window.localStorage.getItem(STORE_KEY)) || {};
    } catch (ignore) {
        return {};
    }
}

function fetchJson(url) {
    return fetch(url).then((response) => {
        if (!response.ok) {
            throw new Error(url);
        }
        return response.json();
    });
}

function makeImage(src, alt, title) {
    const img = document.createElement("img");
    img.src = src || "";
    img.alt = alt || "";
    if (title) {
        img.title = title;
    }
    img.width = 177;
    img.height = 124;
    img.loading = "lazy";
    return img;
}

function makeDetails({ id, open, extraClass }) {
    const details = document.createElement("details");
    details.className = `details edition${extraClass ? ` ${extraClass}` : ""}`;
    details.id = id;
    details.open = open;
    const summary = document.createElement("summary");
    summary.className = "details__summary";
    const heading = document.createElement("h2");
    heading.className = "details__heading";
    summary.append(heading);
    const body = document.createElement("div");
    body.className = "details__body";
    details.append(summary, body);
    return { details, heading, body };
}

function renderGroup(group, jinxesByTarget, i18n) {
    const { details, heading, body } = makeDetails({
        id: `wrapper-${group.teamId}`,
        open: OPEN_TEAMS.includes(group.teamId),
        extraClass: group.characters.length ? "" : "is-empty",
    });

    const headingText = document.createElement("span");
    headingText.className = "edition__heading-text";
    headingText.textContent = group.name;
    heading.append(headingText);

    const team = document.createElement("div");
    team.id = `team-${group.teamId}`;
    team.className = "edition__team";

    group.characters.forEach((character) => {
        const role = document.createElement("div");
        role.className = "edition__role";

        const name = document.createElement("p");
        name.className = "edition__name";
        name.textContent = character.name;

        const icon = document.createElement("div");
        icon.className = "edition__icon";
        const image = makeImage(character.image, "");
        image.classList.add("edition__image");
        image.setAttribute("aria-hidden", "true");
        icon.append(image);

        const ability = document.createElement("div");
        ability.className = "edition__ability";
        ability.textContent = character.ability || "";

        role.append(name, icon, ability);

        const jinxes = jinxesByTarget[normaliseId(character.id)] || [];
        if (jinxes.length) {
            const wrap = document.createElement("div");
            wrap.className = "edition__jinxes";
            jinxes.forEach((jinx) => {
                const cell = document.createElement("div");
                cell.className = "edition__jinx";
                cell.append(makeImage(jinx.trick.image, `${i18n.jinx}: ${jinx.trick.name}`, jinx.reason));
                wrap.append(cell);
            });
            role.append(wrap);
        }

        team.append(role);
    });

    body.append(team);
    return details;
}

function renderJinxList(jinxes, i18n) {
    const { details, heading, body } = makeDetails({
        id: "wrapper-jinx",
        open: true,
        extraClass: "edition--jinx",
    });

    const headingText = document.createElement("span");
    headingText.className = "edition__heading-text";
    headingText.textContent = i18n.jinxesTitle;
    heading.append(headingText);

    const team = document.createElement("div");
    team.id = "team-jinx";
    team.className = "edition__team";

    jinxes.forEach((jinx) => {
        const role = document.createElement("div");
        role.className = "edition__role edition__role--jinx";

        const target = document.createElement("div");
        target.className = "edition__icon edition__icon--target";
        target.append(makeImage(jinx.target.image, jinx.target.name));

        const trick = document.createElement("div");
        trick.className = "edition__icon edition__icon--trick";
        trick.append(makeImage(jinx.trick.image, jinx.trick.name));

        const reason = document.createElement("div");
        reason.className = "edition__ability";
        reason.textContent = jinx.reason;

        role.append(target, trick, reason);
        team.append(role);
    });

    body.append(team);
    return details;
}

export default function renderSheet() {

    const i18n = window.SHEET_I18N || { jinxesTitle: "Jinxes", jinx: "Jinx" };
    const base = `/build/data/${localeSegment()}`;

    // Plain Promise chain (no async/await) to avoid pulling in regenerator-
    // runtime, which the sheet entry doesn't bundle.
    return Promise.all([
        fetchJson(`${base}/characters.json`),
        fetchJson(`${base}/jinx.json`),
        fetchJson(`${base}/teams.json`),
    ]).then(([characters, jinxFeed, teams]) => {

    const officialById = {};
    characters.forEach((c) => { officialById[normaliseId(c.id)] = c; });
    const teamNames = {};
    teams.forEach((t) => { teamNames[t.id] = t.name; });

    const params = new URLSearchParams(window.location.search);
    const stored = readStore().characters || {};
    const storedList = Array.isArray(stored.characters) ? stored.characters : [];

    const customById = {};
    storedList.forEach((item) => {
        if (item && typeof item === "object") {
            customById[normaliseId(item.id)] = item;
        }
    });

    let ids;
    let name;
    const charsParam = params.get("characters");
    if (charsParam) {
        ids = charsParam.split(",").map(normaliseId).filter(Boolean);
        name = params.get("name") || stored.name || "";
    } else {
        ids = storedList
            .map((item) => normaliseId(typeof item === "string" ? item : item && item.id))
            .filter(Boolean);
        name = stored.name || "";
    }

    const idSet = new Set(ids);
    const recordById = {};
    const resolved = [];
    ids.forEach((id) => {
        const source = officialById[id] || customById[id];
        if (source) {
            const record = Object.assign({}, source, { id });
            resolved.push(record);
            recordById[id] = record;
        }
    });

    // Active jinxes — pairs where both characters are in the selection.
    const jinxes = [];
    const seen = new Set();
    function addJinx(targetId, trickId, reason) {
        const key = `${targetId}|${trickId}`;
        if (!idSet.has(targetId) || !idSet.has(trickId) || seen.has(key)) {
            return;
        }
        const target = recordById[targetId];
        const trick = recordById[trickId];
        if (target && trick) {
            seen.add(key);
            jinxes.push({ target, trick, reason });
        }
    }

    jinxFeed.forEach((group) => {
        const targetId = normaliseId(group.id);
        group.jinx.forEach((j) => addJinx(targetId, normaliseId(j.id), j.reason));
    });
    resolved.forEach((character) => {
        const custom = customById[normaliseId(character.id)];
        if (custom && Array.isArray(custom.jinxes)) {
            custom.jinxes.forEach((j) => {
                if (j && typeof j === "object") {
                    addJinx(normaliseId(character.id), normaliseId(j.id), j.reason);
                }
            });
        }
    });

    const jinxesByTarget = {};
    jinxes.forEach((jinx) => {
        const targetId = normaliseId(jinx.target.id);
        (jinxesByTarget[targetId] = jinxesByTarget[targetId] || []).push(jinx);
    });

    const groups = TEAM_ORDER
        .map((teamId) => ({
            teamId,
            name: teamNames[teamId] || teamId,
            characters: resolved.filter((c) => c.team === teamId),
        }))
        .filter((group) => group.characters.length);

    // Inject into the page.
    const title = document.getElementById("title");
    if (title) {
        title.textContent = name;
    }
    if (name) {
        document.title = `${name} - ${document.title.split(" - ").pop()}`;
    }

    const content = document.getElementById("sheet-content");
    if (!content) {
        return;
    }
    content.textContent = "";
    groups.forEach((group) => content.append(renderGroup(group, jinxesByTarget, i18n)));
    if (jinxes.length) {
        content.append(renderJinxList(jinxes, i18n));
    }

    }).catch(() => { /* no data feeds available — leave the static shell */ });
}
